import React, { useEffect, useState } from "react";
import { Link } from "react-scroll";
import { HiMenuAlt3, HiX } from "react-icons/hi";
import { motion, AnimatePresence } from "framer-motion";

function Navbar() {
  const [isScrolledUp, setIsScrolledUp] = useState(true);
  const [isTop, setIsTop] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  let lastScrollTop = 0;

  const navItems = [
    { name: "Početna", to: "hero", isExternal: false },
    { name: "O nama", to: "about", isExternal: false },
    { name: "Usluge", to: "services", isExternal: false },
    { name: "Kontakt", to: "contact", isExternal: false }
  ];

  const handleNavClick = (item) => {
    if (item.isExternal) {
      window.open(item.to, '_blank');
    }
    setIsMenuOpen(false);
  };

  // Lock body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isMenuOpen]);

  useEffect(() => {
    const handleScroll = () => {
      const currentScroll = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolledUp(currentScroll <= lastScrollTop || currentScroll <= 0);
      setIsTop(currentScroll <= 10);
      lastScrollTop = currentScroll <= 0 ? 0 : currentScroll;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 ease-in-out
                ${isScrolledUp ? "translate-y-0" : "-translate-y-full"}
                ${isTop ? "bg-transparent" : "bg-white/90 shadow-lg backdrop-blur-sm"}`}
    >
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link
            to="hero"
            spy={true}
            smooth={true}
            duration={500}
            className="text-2xl font-bold tracking-tight z-50 hover:scale-105 
                     transition-transform duration-300 cursor-pointer"
            onClick={() => setIsMenuOpen(false)}
          >
            <span className="text-primary-600">Lux</span>
            <span className={isTop && !isMenuOpen ? "text-white" : "text-gray-800"}>
              Energy
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <div key={item.name} className="group relative">
                {item.isExternal ? (
                  <a
                    href={item.to}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`font-medium transition-all duration-300 cursor-pointer
                              hover:scale-105 relative pb-1
                              after:absolute after:bottom-0 after:left-0 after:h-[2px]
                              after:w-0 after:transition-all after:duration-300
                              ${
                                isTop
                                  ? "text-white hover:text-primary-300"
                                  : "text-gray-700 hover:text-primary-600 after:bg-primary-600 hover:after:w-full"
                              }`}
                  >
                    {item.name}
                  </a>
                ) : (
                  <Link
                    to={item.to}
                    spy={true}
                    smooth={true}
                    duration={500}
                    activeClass={isTop ? "!text-primary-300" : "!text-primary-600 after:!w-full"}
                    className={`font-medium transition-all duration-300 cursor-pointer
                              hover:scale-105 relative pb-1
                              after:absolute after:bottom-0 after:left-0 after:h-[2px]
                              after:w-0 after:transition-all after:duration-300
                              ${
                                isTop
                                  ? "text-white hover:text-primary-300"
                                  : "text-gray-700 hover:text-primary-600 after:bg-primary-600 hover:after:w-full"
                              }`}
                  >
                    {item.name}
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden z-50 p-2 rounded-lg hover:bg-gray-100/10
                     transition-colors duration-300"
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <HiX className={`w-6 h-6 ${isTop && !isMenuOpen ? "text-white" : "text-gray-800"}`} />
            ) : (
              <HiMenuAlt3 className={`w-6 h-6 ${isTop ? "text-white" : "text-gray-800"}`} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[100] md:hidden bg-white"
          >
            {/* Menu Content */}
            <div className="relative flex flex-col items-center justify-center min-h-screen px-4 bg-white">
              {/* Close Button */}
              <button
                onClick={() => setIsMenuOpen(false)}
                className="absolute top-4 right-4 p-3 rounded-full 
                          bg-white shadow-lg hover:bg-primary-50
                          transition-colors duration-300"
                aria-label="Close menu"
              >
                <HiX className="w-6 h-6 text-gray-800" />
              </button>

              {/* Menu Items */}
              <div className="w-full max-w-md space-y-4">
                {navItems.map((item, index) => (
                  <motion.div
                    key={item.name}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="w-full"
                  >
                    {item.isExternal ? (
                      <a
                        href={item.to}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => setIsMenuOpen(false)}
                        className="block w-full text-xl font-medium text-gray-800 
                                 px-6 py-4 rounded-xl hover:bg-gray-50
                                 transition-colors duration-300"
                      >
                        {item.name}
                      </a>
                    ) : (
                      <Link
                        to={item.to}
                        spy={true}
                        smooth={true}
                        duration={500}
                        onClick={() => setIsMenuOpen(false)}
                        className="block w-full text-xl font-medium text-gray-800 
                                 px-6 py-4 rounded-xl hover:bg-gray-50
                                 transition-colors duration-300"
                      >
                        {item.name}
                      </Link>
                    )}
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

export default Navbar;
